import {Download} from 'lucide-react'

export interface ScheduleRow {
  year: number
  beginningValue: number
  depreciation: number
  accumulatedDepreciation: number
  endingBookValue: number
}

interface Props {
  rows: ScheduleRow[]
  onExport: () => void
}

function fmt(n: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n)
}

export default function DepreciationScheduleTable({ rows, onExport }: Props) {
  return (
    <div className="mt-8">
      {/* Export CTA */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Depreciation Schedule</h3>
        <button
          onClick={onExport}
          className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors shadow-sm"
        >
          <Download className="w-4 h-4" />
          Export to Excel (CSV)
        </button>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Download this depreciation schedule format in Excel-compatible CSV for your tax records and Form 4562 filing.
      </p>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="w-full text-sm">
          <thead className="bg-gray-800 text-white">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">Year</th>
              <th className="px-4 py-3 text-right font-semibold">Beginning Value</th>
              <th className="px-4 py-3 text-right font-semibold">Depreciation</th>
              <th className="px-4 py-3 text-right font-semibold">Accumulated Depr.</th>
              <th className="px-4 py-3 text-right font-semibold">Ending Book Value</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={row.year} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                <td className="px-4 py-3 font-medium text-blue-700">Year {row.year}</td>
                <td className="px-4 py-3 text-right text-gray-700">{fmt(row.beginningValue)}</td>
                <td className="px-4 py-3 text-right text-green-700 font-medium">{fmt(row.depreciation)}</td>
                <td className="px-4 py-3 text-right text-gray-600">{fmt(row.accumulatedDepreciation)}</td>
                <td className="px-4 py-3 text-right text-gray-900 font-medium">{fmt(row.endingBookValue)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-gray-100 font-semibold">
            <tr>
              <td className="px-4 py-3 text-gray-900">Total</td>
              <td className="px-4 py-3 text-right text-gray-600">—</td>
              <td className="px-4 py-3 text-right text-green-700">
                {fmt(rows.reduce((sum, r) => sum + r.depreciation, 0))}
              </td>
              <td className="px-4 py-3 text-right text-gray-600">
                {fmt(rows[rows.length - 1]?.accumulatedDepreciation ?? 0)}
              </td>
              <td className="px-4 py-3 text-right text-gray-900">
                {fmt(rows[rows.length - 1]?.endingBookValue ?? 0)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  )
}
